import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { formatDistanceToNowStrict } from "date-fns"
import { CloudOff, CreditCard, ExternalLink, Gauge, Info, KeyRound, Loader2, RefreshCw, ServerCrash, WifiOff, type LucideIcon } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { useAiHealth, useCheckAiHealth } from "@/hooks/use-ai-health"
import { useOnlineStatus } from "@/hooks/use-online-status"
import { API_KEYS_SETTINGS, BILLING_URLS, isNetworkError, type AiHealth } from "@/lib/ai-health"
import { ApiError } from "@/lib/api-client"
import { cn } from "@/lib/utils"

type Fix = "billing" | "keys"

interface Issue {
  /** Identity of the problem; a change from a key to none means it was solved. */
  key: string
  tone: "error" | "warning"
  icon: LucideIcon
  title: string
  message: string
  /** Toast shown once the problem is gone. */
  recovered: string
  fixes: Fix[]
  health?: AiHealth
}

const FALLBACK_PROBLEM: Partial<Record<AiHealth["status"], string>> = {
  credits: "is out of credits",
  auth: "rejected the API key",
  rate_limit: "is rate-limited",
}

function describe(online: boolean, error: unknown, health: AiHealth | undefined): Issue | null {
  if (!online) {
    return {
      key: "offline",
      tone: "warning",
      icon: WifiOff,
      title: "You're offline",
      message: "Nothing can be saved and AI features can't run until your internet connection is back.",
      recovered: "Back online",
      fixes: [],
    }
  }
  if (error && (isNetworkError(error) || (error instanceof ApiError && error.status >= 500))) {
    return {
      key: "api",
      tone: "warning",
      icon: ServerCrash,
      title: "Can't reach the API server",
      message: "Saving, publishing and AI features won't work until the admin can connect again. Retrying automatically.",
      recovered: "Connected to the API server again",
      fixes: [],
    }
  }
  if (!health || health.severity === "ok") return null

  const name = health.providerName || "The AI provider"
  const base = { key: `${health.provider}:${health.status}`, health, recovered: `${name} is working again` }
  const fallback = health.fallbackProviderName
  if (fallback) {
    // The configured provider is down but the pipeline switched to another one: AI keeps working, so amber.
    const problem = FALLBACK_PROBLEM[health.status] ?? "can't be reached"
    return {
      ...base,
      key: `${base.key}:${health.fallbackProvider}`,
      tone: "warning",
      icon: health.status === "credits" ? CreditCard : health.status === "auth" ? KeyRound : CloudOff,
      title: `${name} ${problem}, using ${fallback} meanwhile`,
      message: `AI work continues on ${fallback} for now. ${health.status === "credits" ? `Add credits to ${name}` : `Fix ${name}`} to switch back; this warning clears by itself once it works again.`,
      recovered: `${name} is working again`,
      fixes: health.status === "credits" ? ["billing", "keys"] : ["keys"],
    }
  }
  switch (health.status) {
    case "credits":
      return {
        ...base,
        tone: "error",
        icon: CreditCard,
        title: `${name} is out of credits`,
        message: `AI writing, Trend Explorer analysis and autopilot are paused. Add credits to your ${name} account; this warning clears by itself within a minute of the top-up.`,
        fixes: ["billing", "keys"],
      }
    case "auth":
      return {
        ...base,
        tone: "error",
        icon: KeyRound,
        title: `${name} rejected the API key`,
        message: `Every ${name} key was refused (invalid, revoked or without access). AI features are stopped until you replace it.`,
        fixes: ["keys"],
      }
    case "no_key":
      return {
        ...base,
        tone: "error",
        icon: KeyRound,
        title: `No ${name} API key`,
        message: `AI features are off until you add a key or switch to another provider.`,
        fixes: ["keys"],
      }
    case "rate_limit":
      return {
        ...base,
        tone: "warning",
        icon: Gauge,
        title: `${name} is rate-limited`,
        message: "Too many requests right now, so AI jobs are slowed down or waiting. It clears by itself; another key raises the limit.",
        fixes: ["keys"],
      }
    default:
      return {
        ...base,
        tone: "warning",
        icon: CloudOff,
        title: `Can't reach ${name}`,
        message: `The server can't connect to ${name} (a network problem or a ${name} outage), so AI jobs fail for now. Retrying automatically.`,
        fixes: [],
      }
  }
}

function ago(iso: string | null | undefined) {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : formatDistanceToNowStrict(date, { addSuffix: true })
}

/**
 * App-wide warning for anything that stops AI work or saving: no AI credits, a rejected or missing key, rate
 * limits, the provider or the API server unreachable, or no internet. It sits under the header on every page,
 * can't be dismissed, and goes away by itself (with a short "working again" toast) once the problem is solved.
 */
export function AiHealthBanner() {
  const online = useOnlineStatus()
  const health = useAiHealth()
  const check = useCheckAiHealth()
  const [showDetail, setShowDetail] = useState(false)
  const issue = describe(online, health.error, health.data)

  // Announce recovery once, when a problem that was showing goes away.
  const shown = useRef<Issue | null>(null)
  useEffect(() => {
    const previous = shown.current
    if (previous && !issue) toast.success(previous.recovered)
    shown.current = issue
    if (!issue) setShowDetail(false)
    // Only the problem's identity matters here, not each poll's new object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue?.key])

  if (!issue) return null

  const recheck = () => {
    if (!issue.health) {
      void health.refetch()
      return
    }
    check.mutate(undefined, {
      onSuccess: (next) => {
        if (next.severity !== "ok") toast.warning("Still not working", { description: "Checked just now. The warning clears by itself once it's fixed." })
      },
    })
  }
  const checking = check.isPending || health.isFetching || Boolean(health.data?.checking)
  const since = ago(issue.health?.since)
  const checked = ago(issue.health?.checkedAt)
  const billing = issue.health ? BILLING_URLS[issue.health.provider] : undefined
  const Icon = issue.icon

  return (
    <div
      role={issue.tone === "error" ? "alert" : "status"}
      className={cn(
        "relative shrink-0 border-b px-3 py-2.5 sm:px-4",
        issue.tone === "error" ? "border-destructive/30 bg-destructive/10" : "border-warning/30 bg-warning/10",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-1 basis-72 items-start gap-2.5">
          <span
            className={cn(
              "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md",
              issue.tone === "error" ? "bg-destructive/15 text-destructive" : "bg-warning/15 text-warning-foreground dark:text-warning",
            )}
            aria-hidden
          >
            <Icon className="size-3.5" />
          </span>
          <div className="min-w-0">
            <p className="text-sm leading-5 font-medium">
              {issue.title}
              {since && <span className="font-normal text-muted-foreground"> · since {since}</span>}
            </p>
            <p className={cn("text-xs leading-relaxed text-muted-foreground", !showDetail && "max-sm:line-clamp-2")}>{issue.message}</p>
          </div>
        </div>

        {/* Phones: one row of actions; Check again and Details shrink to icons. */}
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 max-sm:w-full max-sm:pl-8.5 sm:gap-2">
          {issue.fixes.includes("billing") && billing && (
            <Button size="sm" variant={issue.tone === "error" ? "destructive" : "default"} asChild>
              <a href={billing} target="_blank" rel="noreferrer">
                Add credits <ExternalLink />
              </a>
            </Button>
          )}
          {issue.fixes.includes("keys") && (
            <Button size="sm" variant={issue.fixes.includes("billing") ? "outline" : issue.tone === "error" ? "destructive" : "outline"} asChild>
              <Link to={API_KEYS_SETTINGS}>
                <KeyRound /> API keys
              </Link>
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={recheck} disabled={checking} title="Check again">
            {checking ? <Loader2 className="animate-spin motion-reduce:animate-none" /> : <RefreshCw />}
            <span className="max-sm:sr-only">{checking ? "Checking…" : "Check again"}</span>
          </Button>
          {issue.health?.detail && (
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground"
              aria-expanded={showDetail}
              title={showDetail ? "Hide details" : "Details"}
              onClick={() => setShowDetail((v) => !v)}
            >
              <Info />
              <span className="max-sm:sr-only">{showDetail ? "Hide details" : "Details"}</span>
            </Button>
          )}
        </div>
      </div>

      {showDetail && issue.health && (
        <div className="mt-2 space-y-1 pl-8.5 text-xs text-muted-foreground">
          <p className="max-h-24 overflow-y-auto rounded-md border bg-background/60 px-2.5 py-1.5 font-mono text-[11px] break-words">{issue.health.detail}</p>
          <p>
            {issue.health.providerName} · {issue.health.keys.active} active {issue.health.keys.active === 1 ? "key" : "keys"}
            {issue.health.keys.parked > 0 && `, ${issue.health.keys.parked} parked`}
            {issue.health.keys.env && (issue.health.keys.envParked ? ", .env key parked" : ", .env key")}
            {checked && ` · last checked ${checked}`}
          </p>
        </div>
      )}
    </div>
  )
}
